import {
  createIdentitySession,
  publishIdentity,
  createRetentionProvider,
  toAyDimension,
  type ConsentState,
  type IdentitySession,
} from '@redbrickmedia/duplex.user-identity'
import { OpenFeature } from '@openfeature/web-sdk'
import { useIdentityAnalytics } from '../composables/analytics/useIdentityAnalytics'
import {
  identityConsentState,
  subscribeToConsent,
  type ConsentDecision,
} from '../utils/consent/consentGate'
import {
  markConsentTiming,
  measureConsentTiming,
} from '../utils/consent/consentTiming'
import debug from '../utils/console'
import { queueCustomPatch } from '../utils/ad-ops/assertive-yield/customDimensions'
import { HEM_PROVIDER_DIMENSION } from '../utils/ad-ops/assertive-yield/dimensions'
import { withAyManager } from '../utils/ad-ops/assertive-yield/queue'

const logger = debug.withTag('plugins:user-identity')

export default defineNuxtPlugin({
  name: 'user-identity',
  dependsOn: ['openfeature', 'user-segment'],
  parallel: true,
  setup: async () => {
    markConsentTiming('identity-plugin-start', true)
    const { hemProvider, hemScriptLoaded } = useIdentityAnalytics()

    // `client` answers from the server render while hydrating, and the server
    // evaluates without geo, so a Country rule on this flag never matches there.
    const { client, browser } = await useOpenFeature()
    if (!(browser ?? client).getBooleanValue('enableUserIdentity', false)) {
      markConsentTiming('identity-disabled', true)
      return
    }

    const { targetingKey } = OpenFeature.getContext()
    if (typeof targetingKey !== 'string' || !targetingKey) {
      logger.warn('No targetingKey available; skipping identity capture')
      return
    }

    let session: IdentitySession | null = null
    let sessionHasProvider = false
    let identityAllowed = false
    let lastState: ConsentState | null = null

    const createSession = (state: ConsentState): IdentitySession | null => {
      try {
        const withProvider = state === 'allow'
        const created = createIdentitySession({
          userId: targetingKey,
          consentState: state,
          provider: withProvider
            ? createRetentionProvider({
                onEvent: (event) => {
                  if (event.type === 'script:load') {
                    hemScriptLoaded.value = event.success
                  }
                },
              })
            : undefined,
        })
        sessionHasProvider = withProvider
        return created
      } catch (error) {
        sessionHasProvider = false
        logger.warn('Failed to start identity session', error)
        return null
      }
    }

    const publish = (candidate: IdentitySession) => {
      if (!identityAllowed || session !== candidate) return
      try {
        publishIdentity(candidate, { sendToGAM: false })
      } catch {
        logger.warn('Failed to publish identity to Prebid')
      }
    }

    const syncAyHemProvider = (candidate: IdentitySession) => {
      if (!identityAllowed || session !== candidate) return
      const patch = toAyDimension(candidate.current, {
        dimension: HEM_PROVIDER_DIMENSION,
      })
      hemProvider.value = patch[HEM_PROVIDER_DIMENSION]
      queueCustomPatch(patch)
    }

    const wireSession = (candidate: IdentitySession) => {
      withAyManager(() => publish(candidate))
      syncAyHemProvider(candidate)
      candidate.onHem(() => {
        publish(candidate)
        syncAyHemProvider(candidate)
      })
    }

    const applyConsent = (decision: ConsentDecision) => {
      markConsentTiming('identity-first-decision', true)
      const state = identityConsentState(decision)
      if (state === lastState) return

      identityAllowed = state === 'allow'

      if (!session) {
        if (state === 'unknown') {
          lastState = state
          return
        }
        session = createSession(state)
        if (session) {
          markConsentTiming('identity-session-created', true)
          measureConsentTiming(
            'identity-ready',
            'identity-plugin-start',
            'identity-session-created'
          )
          lastState = state
          if (state === 'allow') wireSession(session)
        }
        return
      }

      if (state === 'allow' && !sessionHasProvider) {
        session = createSession(state)
        if (session) {
          markConsentTiming('identity-provider-session-created', true)
          lastState = state
          wireSession(session)
        }
        return
      }

      try {
        session.revise(state)
      } catch (error) {
        logger.warn('Failed to revise identity consent', error)
        return
      }

      lastState = state
      if (state === 'allow') {
        withAyManager(() => {
          if (session) publish(session)
        })
        if (session) syncAyHemProvider(session)
      } else if (state === 'deny') {
        logger.info(
          `Identity declined (evidence: ${decision.evidence.join(',') || 'none'})`
        )
      }
    }

    subscribeToConsent(applyConsent)
    markConsentTiming('identity-subscribed', true)
  },
})
